/**
 * useVMANotifications Hook
 * Monitors VMA alerts and sends notifications when new alerts appear
 */

import { useEffect, useRef } from 'react';
import { useSettingsStore } from '@/stores/settings-store';
import { useAuthStore } from '@/stores/auth-store';
import { useNotifications } from './useNotifications';
import { getApiUrl } from '@/lib/api';

interface VMAAlert {
  id: string;
  headline: string;
  area: string;
  event: string;
  severity: string;
  sent: string;
}

export function useVMANotifications() {
  const { sendNotification, permissionStatus } = useNotifications();
  const { notifications: notificationSettings, vmaRegion } = useSettingsStore();
  const { accessToken } = useAuthStore();
  const seenAlertsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    // I mobilappen körs webben i en React Native WebView.
    // Där hanteras VMA-pushar nu via Expo/OS (backend), så vi ska inte
    // också schemalägga lokala notiser här för att undvika dubbletter.
    if (typeof window !== 'undefined' && window.ReactNativeWebView) {
      return;
    }

    if (!notificationSettings.vmaAlerts || !permissionStatus.granted || !accessToken) {
      return;
    }

    // Poll for VMA alerts every 5 minutes
    const intervalId = setInterval(async () => {
      try {
        // Build query params for user's region if set
        const params = new URLSearchParams();
        if (vmaRegion && vmaRegion !== 'all') {
          params.set('region', vmaRegion);
        }
        
        const queryString = params.toString();
        const url = getApiUrl(`vma/alerts${queryString ? `?${queryString}` : ''}`);
        
        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        });
        
        if (!response.ok) return;

        const data = await response.json();
        const alerts: VMAAlert[] = data.alerts || [];

        // Check for new alerts
        for (const alert of alerts) {
          if (!seenAlertsRef.current.has(alert.id)) {
            seenAlertsRef.current.add(alert.id);

            // Send notification for new alert
            await sendNotification({
              title: 'Nytt VMA',
              body: `${alert.headline}`,
              data: {
                type: 'vma',
                alertId: alert.id,
                url: '/app/vma',
              },
            });
          }
        }

        // Clean up old alerts from memory (keep last 50)
        if (seenAlertsRef.current.size > 50) {
          const alertIds = Array.from(seenAlertsRef.current);
          seenAlertsRef.current = new Set(alertIds.slice(-50));
        }
      } catch (error) {
        console.error('[useVMANotifications] Failed to check VMA alerts:', error);
      }
    }, 5 * 60 * 1000); // 5 minutes

    // Initial check
    const initCheck = async () => {
      try {
        const params = new URLSearchParams();
        if (vmaRegion && vmaRegion !== 'all') {
          params.set('region', vmaRegion);
        }
        
        const queryString = params.toString();
        const url = getApiUrl(`vma/alerts${queryString ? `?${queryString}` : ''}`);
        
        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        });
        
        if (!response.ok) return;
        
        const data = await response.json();
        const alerts: VMAAlert[] = data.alerts || [];
        // Mark all existing alerts as seen (don't notify on app start)
        alerts.forEach(alert => seenAlertsRef.current.add(alert.id));
      } catch (error) {
        console.error('[useVMANotifications] Failed to initialize VMA alerts:', error);
      }
    };
    
    initCheck();

    return () => {
      clearInterval(intervalId);
    };
  }, [notificationSettings.vmaAlerts, permissionStatus.granted, accessToken, vmaRegion, sendNotification]);
}
